/**
 * ConnectionToast — bandeau discret en bas d'écran qui s'affiche dès que le
 * backend devient inaccessible (réseau coupé, Cloud Run en cold-start, etc.).
 *
 * Trois états :
 *   - online (rien affiché)
 *   - degraded : "Connexion au serveur instable… nouvelle tentative dans Xs"
 *     + bouton "Réessayer maintenant"
 *   - offline : "Pas de connexion internet" (basé sur navigator.onLine)
 */
import React from 'react';
import { useConnectionMonitor } from '../hooks/useConnectionMonitor';


function formatSecondsSince(timestamp) {
  if (!timestamp) return '';
  const sec = Math.floor((Date.now() - timestamp) / 1000);
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min`;
  return `${Math.floor(sec / 3600)} h`;
}


export default function ConnectionToast() {
  const { status, retryCount, lastFailureAt, forceCheck } = useConnectionMonitor();
  // Force un re-render toutes les 1s pour mettre à jour le compteur de durée
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (status === 'online') return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [status]);

  if (status === 'online') return null;

  const isOffline = status === 'offline';
  const duration = formatSecondsSince(lastFailureAt);

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[110] pointer-events-none">
      <div
        className={`pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-2xl border backdrop-blur-xl transition-all ${
          isOffline
            ? 'bg-red-900/90 border-red-500/50'
            : 'bg-amber-900/90 border-amber-500/50'
        }`}
      >
        {/* Indicateur animé */}
        <div className="relative">
          <div className={`w-2.5 h-2.5 rounded-full ${isOffline ? 'bg-red-400' : 'bg-amber-400'}`} />
          <div className={`absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping ${isOffline ? 'bg-red-400' : 'bg-amber-400'}`} />
        </div>

        {/* Texte d'état */}
        <div className="flex flex-col">
          <span className={`text-[11px] font-mono font-bold uppercase tracking-widest ${isOffline ? 'text-red-200' : 'text-amber-200'}`}>
            {isOffline ? 'Hors ligne' : 'Serveur instable'}
          </span>
          <span className={`text-[9px] font-mono ${isOffline ? 'text-red-300/70' : 'text-amber-300/70'}`}>
            {isOffline
              ? 'Vérifie ta connexion internet'
              : `${retryCount} tentative${retryCount > 1 ? 's' : ''}${duration ? ` · ${duration}` : ''}`}
          </span>
        </div>

        {/* Bouton réessayer (uniquement si degraded — offline = OS doit revenir) */}
        {!isOffline && (
          <button
            onClick={forceCheck}
            className="ml-2 px-2.5 py-1 bg-amber-500/30 hover:bg-amber-500/45 border border-amber-400/40 hover:border-amber-400/60 rounded text-[9px] font-mono uppercase tracking-widest text-amber-100 transition-colors"
          >
            Réessayer
          </button>
        )}
      </div>
    </div>
  );
}
